import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { useNativeBrowserLoader } from '~/domains/common/utils/useNativeBrowserLoader';
import { generateAndStoreSingleUseToken } from '~/services/identity/authenticated/identity.service.api';
import { logError } from '~/common/utils/log';

/**
 * Navigates to a contest game-center / external URL.
 *
 * Same-origin URLs use a client-side push. Cross-origin URLs use a full-page
 * navigation. Auth is shared across the splashsports subdomains and EO brand
 * domains, so most destinations need no token handoff — the exception is OFP,
 * whose game-center expects a single-use token appended as `?sut=`. Callers opt
 * in to that handoff with `appendSut` (only OFP contest cards do).
 */
function useRedirectToEO() {
  const [requestedURL, setRequestedURL] = useState<string | null>(null);
  const isRedirecting = useNativeBrowserLoader(); // Used to detect browser redirect
  const router = useRouter();

  const { refetch, isFetching } = useQuery({
    queryKey: ['redirectToEO/generateAndStoreSingleUseToken'],
    queryFn: () => generateAndStoreSingleUseToken(),
    enabled: false,
    cacheTime: 0,
    staleTime: 0,
  });

  const redirectToEO = useCallback(
    async (eoURL: string, newWindow = false, appendSut = false) => {
      // Ignore empty URLs and no-op repeats of an in-flight redirect.
      if (!eoURL || eoURL === requestedURL) return;

      let parsedURL: URL;
      try {
        parsedURL = new URL(eoURL);
      } catch {
        logError({
          errorMessage: 'Invalid redirect URL',
          context: 'useRedirectToEO',
          severity: 'error',
          details: { eoURL },
        });
        return;
      }

      // A true same-origin URL can stay in-app with a client-side push.
      if (parsedURL.origin === window.location.origin) {
        router.push(parsedURL.pathname + parsedURL.search);
        setRequestedURL(null);
        return;
      }

      setRequestedURL(eoURL);

      const target = newWindow ? '_blank' : '_self';

      // OFP handoff: mint a single-use token and append it. On any failure, fall
      // back to a token-less navigation rather than blocking the redirect.
      if (appendSut) {
        try {
          const response = await refetch();
          const sut = response?.data?.data?.token;
          if (sut) parsedURL.searchParams.set('sut', sut);
        } catch (error) {
          logError('Failed to generate SUT for game center redirect:', error);
        }
      }

      window.open(parsedURL.toString(), target);
    },
    [refetch, requestedURL, router]
  );

  // Reset requestedURL when component unmounts (i.e. before redirect)
  useEffect(
    () => () => {
      setRequestedURL(null);
    },
    []
  );

  return {
    redirectToEO,
    isFetching: isFetching || isRedirecting,
    isRedirecting,
    requestedURL,
  };
}

export default useRedirectToEO;
